import React from 'react'

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
}

const cardStyle = {
  width: 420, maxWidth: '92vw', background: '#fff', borderRadius: 14,
  boxShadow: '0 20px 60px rgba(0,0,0,.25)', overflow: 'hidden',
  animation: 'slideInRight .2s ease',
}

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
    <span style={{ color: '#6B7280' }}>{label}</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value || '—'}</span>
  </div>
)

// "12 Hours" → "12 hrs", "1 Hour" → "1 hr"
const formatCheckWindow = (checkTime) => {
  if (!checkTime) return '12 hrs'
  const m = String(checkTime).match(/^(\d+)\s*Hours?$/i)
  if (!m) return checkTime
  return `${m[1]} ${m[1] === '1' ? 'hr' : 'hrs'}`
}

const ChoiceCard = ({ label, icon, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 10, padding: '18px 12px', borderRadius: 10,
      border: '1px solid #E0E7EF', background: '#F8FAFC',
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
    }}
  >
    <div style={{
      width: 40, height: 40, borderRadius: 10, background: '#EFF6FF',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
    }}>
      {icon}
    </div>
    <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{label}</span>
  </button>
)

/**
 * Shown when an inbound call connects. Looks up whether the caller already
 * has a recent open ticket and, based on that, either offers a single
 * "Log as Follow-up" action or a New Ticket / Query choice.
 */
const IncomingCallActionModal = ({ activeCall, recentTicketCheck, submitting, result, onFollowUp, onNewTicket, onQuery }) => {
  if (!activeCall) return null

  const loading = !recentTicketCheck || recentTicketCheck.loading
  const ticket = recentTicketCheck?.ticket || null
  const info = ticket?.ticket_information || {}
  const rider = ticket?.rider_details || {}
  const vehicle = ticket?.vehicle_details || {}
  const status = String(info.status || '').toLowerCase()
  const isBlockingTicket = !!ticket && status !== 'closed' && status !== 'resolved'

  const customerName = rider.name || activeCall.customerName || activeCall.customerPhone || '—'
  const phone = rider.number || activeCall.customerPhone || '—'
  // vin_number comes from the ticket when one exists; otherwise the API still
  // returns a bare top-level "vin" (e.g. {"ticket":null,"vin":"..."})
  const vin = vehicle.vin_number || recentTicketCheck?.vin || ''
  const createdOn = [info.ticket_created_date, info.created_time].filter(Boolean).join(' · ')
  const lastCall = loading ? '—' : (isBlockingTicket ? (createdOn || `within ${formatCheckWindow(recentTicketCheck?.checkTime)}`) : 'No recent ticket')

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ padding: '16px 18px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#16A34A', animation: 'pulse 1.2s infinite' }} />
            <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Active call connected</span>
          </div>
        </div>

        <div style={{ padding: '10px 18px 18px' }}>
          {/* Customer info */}
          <div style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
            <InfoRow label="Customer" value={customerName} />
            <InfoRow label="Phone" value={phone} />
            <InfoRow label="Vin Number" value={vin} />
            <InfoRow label="Ticket Created" value={lastCall} />
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 13, color: '#6B7280' }}>
              Checking for recent tickets…
            </div>
          )}

          {!loading && isBlockingTicket && (
            <>
              <div style={{
                background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
                padding: '12px 14px', marginBottom: 14,
              }}>
                <div style={{ fontWeight: 500, fontSize: 12, color: '#92400E', marginBottom: 8 }}>
                  ⚠ Follow-up detected — no new ticket will be created
                </div>
                <InfoRow label="Ticket" value={info.ticket_number} />
                <InfoRow label="Category" value={info.category} />
                <InfoRow label="Sub-category" value={info.sub_category} />
                <InfoRow label="Status" value={info.status} />
              </div>

              {result && (
                <div style={{
                  marginBottom: 10, padding: '9px 12px', borderRadius: 8, fontSize: 12.5,
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                  background: result.ok ? '#F0FDF4' : '#FEF2F2',
                  border: `1px solid ${result.ok ? '#BBF7D0' : '#FECACA'}`,
                  color: result.ok ? '#166534' : '#991B1B',
                }}>
                  <span>{result.ok ? '✓' : '✕'} {result.message}</span>
                  {result.url && (
                    <button
                      type="button"
                      onClick={() => window.open(result.url, '_blank', 'noopener')}
                      style={{ background: 'none', border: 'none', color: '#0090D9', fontWeight: 700, cursor: 'pointer', fontSize: 12.5, textDecoration: 'underline', padding: 0 }}
                    >
                      Open Ticket ↗
                    </button>
                  )}
                </div>
              )}

              {!result?.ok && (
                <button
                  type="button"
                  onClick={onFollowUp}
                  disabled={submitting === 'followup'}
                  style={{
                    width: '100%', padding: '12px 0', borderRadius: 8, border: 'none',
                    background: '#0090D9', color: '#fff', fontWeight: 700, fontSize: 13,
                    cursor: submitting === 'followup' ? 'default' : 'pointer',
                    opacity: submitting === 'followup' ? 0.7 : 1,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                  }}
                >
                  <span>{submitting === 'followup' ? 'Logging…' : (result ? 'Retry' : 'Log as Follow-up')}</span>
                  <span style={{ fontSize: 11, fontWeight: 400, opacity: .85 }}>Link this call to the existing ticket</span>
                </button>
              )}
            </>
          )}

          {!loading && !isBlockingTicket && (
            <>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#111827', marginBottom: 10 }}>
                What is this call for?
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <ChoiceCard
                  label="New Ticket"
                  icon="🎫"
                  onClick={() => onNewTicket()}
                  disabled={!!submitting}
                />
                <ChoiceCard
                  label="Query"
                  icon="💬"
                  onClick={() => onQuery()}
                  disabled={!!submitting}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default IncomingCallActionModal
